import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal, Typography, Card, List } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useStore } from '../../../helpers/use-store';
import { useNotification } from '../../../hooks/use-notification';
import { usePollUserState } from '../../../hooks/use-poll-user-state';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

export interface PlanChangeModalActions {
  open: (targetPlanId: string) => void;
}

const PlanChangeModal = forwardRef<PlanChangeModalActions>((_, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [targetPlanId, setTargetPlanId] = useState<string>('');
  const { loadingState, updateLoadingState } = useLoading();
  const notificationApi = useNotification();
  const {
    dataStore: { planStore, userStore },
  } = useStore();
  const { startPolling } = usePollUserState();

  useImperativeHandle(ref, () => ({
    open: (planId: string) => {
      setTargetPlanId(planId);
      setIsOpen(true);
    },
  }));

  const currentPlan = planStore.items.find(
    (plan) => plan._id === userStore.userSubscription.planId
  );
  const targetPlan = planStore.items.find((plan) => plan._id === targetPlanId);
  const isUpgrade = (targetPlan?.price || 0) > (currentPlan?.price || 0);

  const handleOk = async () => {
    try {
      updateLoadingState(LoadingState.Loading);
      await userStore.changePlan(targetPlanId);

      // Provider webhook is async — poll until the new plan/credits land.
      startPolling();

      notificationApi.success({
        placement: 'top',
        message: t('billing.planChangeModal.successTitle'),
        description: isUpgrade
          ? t('billing.planChangeModal.upgradeSuccess')
          : t('billing.planChangeModal.downgradeSuccess'),
      });

      handleCancel();
    } catch (e) {
      console.error('Failed to change plan:', e);
      notificationApi.error({
        placement: 'top',
        message: t('billing.planChangeModal.errorTitle'),
        description: t('billing.planChangeModal.errorDesc'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setTargetPlanId('');
  };

  if (!targetPlan || !currentPlan) {
    return null;
  }

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
            {isUpgrade
              ? t('billing.planChangeModal.upgradeTitle')
              : t('billing.planChangeModal.downgradeTitle')}
          </Title>
          <Text type="secondary">
            {isUpgrade
              ? t('billing.planChangeModal.upgradeSubtitle')
              : t('billing.planChangeModal.downgradeSubtitle')}
          </Text>
        </div>
      }
      open={isOpen}
      okText={
        isUpgrade
          ? t('billing.planChangeModal.upgradeConfirm')
          : t('billing.planChangeModal.downgradeConfirm')
      }
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{
        loading: loadingState === LoadingState.Loading,
        type: isUpgrade ? 'primary' : 'default',
      }}
      width={600}
    >
      <ul style={{ paddingLeft: '20px' }}>
        {isUpgrade ? (
          <>
            <li>
              {t('billing.planChangeModal.upgradePoint1', {
                plan: targetPlan.name,
              })}
            </li>
            <li>{t('billing.planChangeModal.upgradePoint2')}</li>
            <li>{t('billing.planChangeModal.upgradePoint3')}</li>
            <li>{t('billing.planChangeModal.upgradePoint4')}</li>
            <li>{t('billing.planChangeModal.upgradePoint5')}</li>
          </>
        ) : (
          <>
            <li>
              {t('billing.planChangeModal.downgradePoint1', {
                plan: currentPlan.name,
              })}
            </li>
            <li>
              {t('billing.planChangeModal.downgradePoint2', {
                plan: targetPlan.name,
              })}
            </li>
            <li>{t('billing.planChangeModal.downgradePoint3')}</li>
          </>
        )}
      </ul>
    </Modal>
  );
});

export default PlanChangeModal;
